import { GiftOutlined } from '@ant-design/icons';
import { useGetLocale, useTranslate } from '@refinedev/core';
import { Badge, Button, Drawer, Empty, List, Tooltip, Typography } from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import React, { useState } from 'react';
import {
  openWhatsNewArticle,
  usePendingWhatsNew,
  useWhatsNewHistory,
} from './useWhatsNew';
import { WhatsNewImage } from './WhatsNewImage';

/**
 * E32 · "Portal — What's New drawer" — every announcement that was ever the therapist's, ONE ROW
 * PER ANNOUNCEMENT (founder round 3: a reading list, not an audit log), newest first, dismissed or
 * not, each with its article. Opened from the header's gift button, which carries a badge with
 * the number still pending. Nothing here acknowledges anything — the banner does that; this is
 * where a person comes back to re-read.
 */
export const WhatsNewDrawerButton: React.FC<{ className?: string }> = ({
  className,
}) => {
  const translate = useTranslate();
  const locale = useGetLocale();
  const isRu = (locale() ?? 'en').startsWith('ru');
  const [open, setOpen] = useState(false);
  const { rows: pending } = usePendingWhatsNew();
  const { rows, isLoading } = useWhatsNewHistory(open);

  return (
    <>
      <Tooltip title={translate('whatsNew.drawer.open')}>
        <Badge count={pending.length} size="small">
          <Button
            aria-label={translate('whatsNew.drawer.open')}
            className={className}
            data-testid="cc-whats-new-open"
            icon={<GiftOutlined />}
            onClick={() => setOpen(true)}
            type="text"
          />
        </Badge>
      </Tooltip>
      <Drawer
        data-testid="cc-whats-new-drawer"
        onClose={() => setOpen(false)}
        open={open}
        title={translate('whatsNew.drawer.title')}
        width={420}
      >
        <List
          dataSource={rows}
          loading={isLoading}
          locale={{
            emptyText: (
              <Empty
                data-testid="cc-whats-new-drawer-empty"
                description={translate('whatsNew.drawer.empty')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          renderItem={(row) => (
            <List.Item
              actions={
                row.articleUrl
                  ? [
                      <Button
                        data-testid={`cc-whats-new-row-read-${row.announcementId}`}
                        key="read"
                        onClick={() => openWhatsNewArticle(row)}
                        size="small"
                        type="link"
                      >
                        {translate('whatsNew.readArticle')}
                      </Button>,
                    ]
                  : undefined
              }
              data-testid={`cc-whats-new-row-${row.announcementId}`}
            >
              <List.Item.Meta
                description={
                  <>
                    <WhatsNewImage
                      alt={row.title}
                      mediaUrl={row.mediaUrl}
                      testId={`cc-whats-new-row-image-${row.announcementId}`}
                      maxHeight={160}
                    />
                    <Typography.Paragraph style={{ margin: '0 0 4px' }}>
                      {row.summary}
                    </Typography.Paragraph>
                    <Typography.Text style={{ fontSize: 12 }} type="secondary">
                      {format(
                        parseISO(row.publishAt),
                        isRu ? 'dd MMM yyyy' : 'MMM dd, yyyy',
                        {
                          locale: isRu ? ru : enUS,
                        },
                      )}
                      {row.relatedReleases.length > 0
                        ? ` · ${translate('whatsNew.releases', {
                            versions: row.relatedReleases.join(', '),
                          })}`
                        : ''}
                    </Typography.Text>
                  </>
                }
                title={row.title}
              />
            </List.Item>
          )}
        />
      </Drawer>
    </>
  );
};
